import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, categoryAllAnime, siteConfig } from '@/mocks/animeData';

const ITEMS_PER_PAGE = 6;

export default function CategoryPage() {
  const { type = 'story' } = useParams<{ type: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const currentCategory = categories.find((c) => c.id === type) || categories[0];

  const filteredAnime = useMemo(
    () => categoryAllAnime.filter((a) => a.category === type),
    [type]
  );

  const totalPages = Math.ceil(filteredAnime.length / ITEMS_PER_PAGE);
  const paginatedAnime = useMemo(
    () => filteredAnime.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [filteredAnime, currentPage]
  );

  const handleCategoryChange = (catId: string) => {
    setCurrentPage(1);
    window.REACT_APP_NAVIGATE(`/category/${catId}`);
  };

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - ${siteConfig.name}`,
    description: `${currentCategory.name}类型成人动漫推荐与导航`,
    url: `${siteConfig.url}/category/${type}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${currentCategory.name}成人动漫推荐｜${siteConfig.name}`}
        description={`浏览${currentCategory.name}类型的精选成人动漫作品推荐。${currentCategory.description}`}
        keywords={`成人动漫,${currentCategory.name},成人动漫推荐,成人动漫类型`}
        canonicalUrl={`${siteConfig.url}/category/${type}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryJsonLd) }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="py-6">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '分类浏览' },
                { name: currentCategory.name },
              ]}
            />
          </div>

          <div className="mb-10">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">
              {currentCategory.name}成人动漫推荐
            </h1>
            <p className="text-sm text-foreground-600">{currentCategory.description}</p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  cat.id === type
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          {filteredAnime.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-2xl text-secondary-500"></i>
              </div>
              <p className="text-foreground-600">该分类下暂无内容，敬请期待</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-10">
                {paginatedAnime.map((anime) => (
                  <Link
                    key={anime.id}
                    to={`/article/${anime.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={anime.image}
                        alt={anime.title}
                        loading="lazy"
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 right-3 bg-primary-500 text-background-50 text-sm font-bold px-2.5 py-1 rounded-md">
                        {anime.rating}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-base text-foreground-900 mb-1.5 group-hover:text-primary-500 transition-colors">
                        {anime.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-2">
                        {anime.description}
                      </p>
                      <span className="text-xs text-foreground-400">{anime.year}</span>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pb-14">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                        page === currentPage
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}